import { Button, Stack } from '@mui/joy';
import { ChangeEvent, useEffect } from 'react';
import { useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { addressService } from 'addresses/services';
import { useApprovedVendorsActions } from 'companySettings/hooks';
import { IApprovedVendor } from 'companySettings/types';
import { contactGroupService } from 'contacts/services';
import { Form, FormField } from 'core/components';
import { FormSectionHeader } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { manufacturerService } from 'parts/services';
import { Color, Level, Variant } from 'theme/enums';

import { IUpsertVendorAssociationForm } from './types';

type IProps = {
  isFormOpen: boolean;
  title: string;
  onClose: () => void;
  onSubmitSuccess: () => void;
  disabledFields?: {
    manufacturer?: boolean;
  };
};

export const AddVendorAssociationForm = ({
  isFormOpen,
  title,
  onClose,
  onSubmitSuccess,
  disabledFields,
}: IProps) => {
  const { t } = useTranslation();

  const { upsertApprovedVendors } = useApprovedVendorsActions();

  const getManufacturerOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
    manufacturerService.getMany(requestPayload, signal);

  const getContactGroupOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
    contactGroupService.getManyAutocomplete(requestPayload, signal);

  const getAddressOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
    addressService.getManyAutocomplete(requestPayload, signal);

  const onSubmit =
    (reset: UseFormReset<IUpsertVendorAssociationForm>) =>
    async (data: IUpsertVendorAssociationForm) => {
      const payload = data.vendorAssociations.map((vendorAssociation) => ({
        manufacturerId: data.manufacturer.id,
        contactGroupAddressId: vendorAssociation.contactGroupAddress.contactGroupAddressId,
        isDefault: vendorAssociation.isDefault ? 1 : 0,
      }));

      const { success } = await upsertApprovedVendors(payload as unknown as IApprovedVendor[]);

      if (success) {
        onSubmitSuccess();
        onClose();
        reset();
      }
    };

  const { reset, control, watch, setValue, resetField } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'vendorAssociations',
  });

  useEffect(() => {
    if (!isFormOpen) {
      reset();
    }
  }, [isFormOpen, reset]);

  return (
    <Form title={title} isOpen={isFormOpen} onClose={onClose} onSubmit={onSubmit}>
      <FormField
        name="manufacturer"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelManufacturer'),
          required: true,
          getOptions: getManufacturerOptions,
          optionKey: 'name',
          disabled: disabledFields?.manufacturer,
        }}
      />

      <>
        {fields.map((field, index) => (
          <Stack key={field.id} gap={0.5}>
            <FormSectionHeader
              title={`${t('labelVendor')} ${index + 1}`}
              level={Level.H4}
              buttons={
                index > 0
                  ? [
                      <Button
                        key="remove"
                        variant={Variant.PLAIN}
                        color={Color.DANGER}
                        onClick={() => remove(index)}
                      >
                        {t('buttonLabelRemove')}
                      </Button>,
                    ]
                  : []
              }
            />

            <FormField
              name={`vendorAssociations.${index}.vendor`}
              type={InputType.AUTOCOMPLETE}
              inputProps={{
                control,
                label: t('labelVendorName'),
                required: true,
                getOptions: getContactGroupOptions,
                onChange: () => resetField(`vendorAssociations.${index}.contactGroupAddress`),
                requestPayload: {
                  filters: {
                    isVendor: 1,
                  },
                },
              }}
            />

            <FormField
              key={watch(`vendorAssociations.${index}.vendor.id`)}
              name={`vendorAssociations.${index}.contactGroupAddress`}
              type={InputType.AUTOCOMPLETE}
              wrapperStyle={{ marginTop: '8px' }}
              inputProps={{
                control,
                label: t('labelVendorLocation'),
                required: true,
                getOptions: getAddressOptions,
                getLabel: (option) =>
                  option.contactGroupAddressAccountsPayableId
                    ? `${option.name} (${option.contactGroupAddressAccountsPayableId})`
                    : option.name,
                requestPayload: {
                  viewBy: {
                    contactGroup: {
                      id: watch(`vendorAssociations.${index}.vendor.id`, fields) ?? 0,
                    },
                  },
                },
              }}
            />

            <FormField
              name={`vendorAssociations.${index}.isDefault`}
              type={InputType.CHECKBOX}
              wrapperStyle={{ marginTop: '8px' }}
              inputProps={{
                control,
                label: t('labelMakeVendorDefault'),
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    fields.forEach((_, idx) => {
                      if (idx !== index) {
                        setValue(`vendorAssociations.${idx}.isDefault`, false);
                      }
                    });
                  }
                },
              }}
            />
          </Stack>
        ))}
      </>

      <Button
        variant={Variant.PLAIN}
        onClick={() => append({ vendor: null, contactGroupAddress: null, isDefault: false })}
        sx={{ width: 'fit-content' }}
      >
        + {t('buttonLabelAddAnotherVendor')}
      </Button>
    </Form>
  );
};
